import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { HbrTab, HbrTabGroup, HbrTabPanel } from '@harbor/elements-react-standalone';
import StbButton from '../security-toolbox-ui/StbButton';
import { useEvaluateLabRun, useLabRun, useLabScenario } from '../../hooks/lab-hooks';
import {
  LabRun,
  labPhaseLabel,
  labRunStateSentiment,
  labRunSummary,
} from '../../support/labService';
import LabDeviceHealthPanel from './LabDeviceHealthPanel';
import LabRunCriteriaPanel from './LabRunCriteriaPanel';
import LabRunStepsTable from './LabRunStepsTable';
import LabRunTriggersPanel from './LabRunTriggersPanel';
import {
  LabCard,
  LabDate,
  LabJsonView,
  LabPanelState,
  LabProgressBar,
  LabStatusTag,
  labErrorMessage,
} from './LabShared';

type Tab = 'steps' | 'criteria' | 'triggers' | 'health' | 'facts';

export type LabRunDetailProps = {
  runId?: number;
  /** Proctor controls: revalidate, report and waive criteria, fire triggers. */
  canProctor?: boolean;
};

/**
 * One run.
 *
 * Progress and score are shown as two separate numbers on purpose. A run can execute every
 * step it has and still not meet its criteria - and a run that fails its criteria while
 * showing 100% progress is the interesting case, not a contradiction to hide.
 *
 * The scenario is fetched alongside so the steps table can say what a blocked step is waiting
 * on: the run's steps carry their own state, but the dependency graph lives on the scenario.
 */
const LabRunDetail: React.FC<LabRunDetailProps> = ({ runId, canProctor = false }) => {
  const params = useParams();
  const navigate = useNavigate();
  const id = runId ?? Number(params.runId);

  const [tab, setTab] = useState<Tab>('steps');
  const [actionError, setActionError] = useState<string | null>(null);

  const { data: run, isLoading, error, refetch } = useLabRun(id);
  const { data: scenario } = useLabScenario(run?.scenario, { enabled: !!run?.scenario });
  const evaluate = useEvaluateLabRun();

  // Memoized because the `|| []` would hand the two memos below a fresh array on every render,
  // rebuilding both maps for a scenario that has not changed.
  const scenarioSteps = useMemo(() => scenario?.steps || [], [scenario?.steps]);

  /** step key -> dependency keys, so "blocked" can name what it is waiting on. */
  const dependencyKeys = useMemo(() => {
    const byId = Object.fromEntries(scenarioSteps.map((step) => [step.id, step.key]));

    return Object.fromEntries(
      scenarioSteps.map((step) => [
        step.key,
        (step.depends_on || []).map((depId) => byId[depId]).filter(Boolean),
      ]),
    );
  }, [scenarioSteps]);

  const sensitiveKeys = useMemo(
    () => scenarioSteps.filter((step) => step.sensitive_output).map((step) => step.key),
    [scenarioSteps],
  );

  const handleEvaluate = async (revalidate: boolean) => {
    if (
      revalidate &&
      // eslint-disable-next-line no-restricted-globals
      !window.confirm(
        'Re-open validation? The validation steps run again against the pod as it is now, and their current results are replaced.',
      )
    ) {
      return;
    }

    setActionError(null);

    try {
      await evaluate.mutateAsync({ runId: id, revalidate });
    } catch (exc) {
      setActionError(labErrorMessage(exc, 'Could not re-evaluate the run.'));
    }
  };

  if (isLoading || error || !run) {
    return (
      <LabCard title="Run">
        <LabPanelState
          loading={isLoading}
          error={error}
          empty={!run}
          // The controller answers 404 rather than 403 for a session you are not part of,
          // because whether a run exists is itself information about somebody else's session.
          emptyMessage="No run here. Either it does not exist, or it belongs to a session you are not part of."
          loadingLabel="Loading run..."
        >
          <span />
        </LabPanelState>
      </LabCard>
    );
  }

  const loaded = run as LabRun;
  const summary = labRunSummary(loaded);

  return (
    <div className="flex flex-col gap-4">
      <LabCard
        title={loaded.scenario_name || loaded.scenario_slug}
        subtitle={
          <span>
            {loaded.session_name || 'session'}
            {` • attempt ${loaded.attempt}`}
            {loaded.for_user_email ? ` • ${loaded.for_user_email}` : ''}
          </span>
        }
        actions={
          <>
            <StbButton role="default" size="small" onClick={async () => navigate('/lab/runs')}>
              All runs
            </StbButton>
            <StbButton role="default" size="small" onClick={async () => refetch()}>
              Refresh
            </StbButton>
            <StbButton
              role="request"
              size="small"
              loading={evaluate.isPending}
              onClick={async () => handleEvaluate(false)}
            >
              Re-evaluate
            </StbButton>
            {canProctor && (
              <StbButton
                role="update"
                size="small"
                loading={evaluate.isPending}
                onClick={async () => handleEvaluate(true)}
              >
                Re-open validation
              </StbButton>
            )}
          </>
        }
      >
        <div className="flex flex-wrap items-center gap-4">
          <LabStatusTag
            label={loaded.state}
            sentiment={labRunStateSentiment(loaded.state)}
            size="medium"
          />
          <LabStatusTag label={labPhaseLabel(loaded.phase)} />

          <div className="flex flex-wrap items-center gap-6">
            <LabProgressBar
              percent={summary.progressPercent}
              label={`Progress ${summary.progressPercent}% - ${summary.progressLabel}`}
            />
            <LabProgressBar
              percent={summary.scorePercent}
              label={`Score ${summary.scorePercent}% - ${summary.scoreLabel}`}
              sentiment={loaded.state === 'passed' ? 'success' : 'info'}
            />
          </div>
        </div>

        {loaded.steps_failed > 0 && (
          <p className="m-0 mt-2 text-sm text-red-700">
            {`${loaded.steps_failed} step${loaded.steps_failed === 1 ? '' : 's'} failed.`}
          </p>
        )}

        {loaded.error_message && (
          <p className="m-0 mt-2 text-sm text-red-700">{loaded.error_message}</p>
        )}

        <div className="mt-3 grid grid-cols-1 gap-2 text-xs text-gray-500 md:grid-cols-3">
          <div>
            {'Started '}
            <LabDate value={loaded.started_at} />
          </div>
          <div>
            {'Last activity '}
            <LabDate value={loaded.last_activity_at} />
          </div>
          <div>
            {'Completed '}
            <LabDate value={loaded.completed_at} />
          </div>
        </div>

        {actionError && <p className="m-0 mt-2 text-sm text-amber-800">{actionError}</p>}
      </LabCard>

      <HbrTabGroup primary>
        <HbrTab slot="nav" panel="steps" active={tab === 'steps'} onClick={() => setTab('steps')}>
          {`Steps (${(loaded.steps || []).length})`}
        </HbrTab>
        <HbrTab
          slot="nav"
          panel="criteria"
          active={tab === 'criteria'}
          onClick={() => setTab('criteria')}
        >
          {`Criteria (${loaded.criteria_met}/${loaded.criteria_total})`}
        </HbrTab>
        <HbrTab
          slot="nav"
          panel="triggers"
          active={tab === 'triggers'}
          onClick={() => setTab('triggers')}
        >
          Triggers
        </HbrTab>
        <HbrTab slot="nav" panel="health" active={tab === 'health'} onClick={() => setTab('health')}>
          Device health
        </HbrTab>
        <HbrTab slot="nav" panel="facts" active={tab === 'facts'} onClick={() => setTab('facts')}>
          Facts
        </HbrTab>

        <HbrTabPanel name="steps">
          {tab === 'steps' && (
            <LabCard title="Steps">
              <LabRunStepsTable
                run={loaded}
                steps={loaded.steps || []}
                dependencyKeys={dependencyKeys}
                sensitiveKeys={sensitiveKeys}
              />
            </LabCard>
          )}
        </HbrTabPanel>

        <HbrTabPanel name="criteria">
          {tab === 'criteria' && (
            <LabCard
              title="Success criteria"
              subtitle={`Passes at ${scenario?.pass_threshold_percent ?? 100}%, and every required criterion has to be met.`}
            >
              <LabRunCriteriaPanel
                run={loaded}
                criteria={loaded.criteria || []}
                canReport={canProctor}
              />
            </LabCard>
          )}
        </HbrTabPanel>

        <HbrTabPanel name="triggers">
          {tab === 'triggers' && (
            <LabCard title="Triggers">
              <LabRunTriggersPanel runId={loaded.id} canFire={canProctor} />
            </LabCard>
          )}
        </HbrTabPanel>

        <HbrTabPanel name="health">
          {tab === 'health' && (
            <LabCard
              title="Device health"
              subtitle="Reported by the agent in the pod on its own poll interval."
            >
              <LabDeviceHealthPanel sessionKey={loaded.session_key} />
            </LabCard>
          )}
        </HbrTabPanel>

        <HbrTabPanel name="facts">
          {tab === 'facts' && (
            <LabCard
              title="Facts"
              subtitle="Values steps pulled out of their output. Fact-match criteria assert against these."
            >
              <LabJsonView value={loaded.facts} />
            </LabCard>
          )}
        </HbrTabPanel>
      </HbrTabGroup>
    </div>
  );
};

export default LabRunDetail;
